import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { GridActionsCellItem, GridEditInputCell, GridRowModes } from '@mui/x-data-grid-premium';
import { INTEGER } from 'utils/helpers/validationMessages';
import { isInt } from 'utils/helpers/formValidations';
import * as R from 'ramda';
import ProductGradeChip from './component/ProductGradeChip';

const minWidth = 150;
const getValidationMessage = (volume, maxVolume) => {
  if (R.isNil(volume)) {
    return 'Volume cannot be empty';
  }
  if (R.not(isInt(volume))) {
    return INTEGER;
  }
  if (R.gt(volume, maxVolume)) {
    return 'Volume cannot exceed volume awarded';
  }
  if (R.gt(0, volume)) {
    return 'Volume cannot be less than 0';
  }
  return '';
};

const AwardedColumns = ({
  handleCancel,
  nominations,
  rowModesModel,
  setCurrentNomId,
  setIsRejectOpen,
  setNominations,
  setRowModesModel,
  submitNomination,
  handleCopyNomination,
  isProductShippingViewOnly,
}) => {
  const handleElect = row => () => {
    const { nominationId, volAccepted, volAwarded } = row;
    setRowModesModel({ ...rowModesModel, [nominationId]: { mode: GridRowModes.Edit } });
    setNominations({
      ...nominations,
      [nominationId]: { hasChanged: false, isValid: true, nominationId, volAccepted, volAwarded },
    });
  };

  const handleSave = id => () => {
    if (nominations[id].volAccepted === 0) {
      setCurrentNomId(id);
      setIsRejectOpen(true);
    } else {
      submitNomination(nominations[id]);
      setRowModesModel(R.mergeLeft({ [id]: { mode: GridRowModes.View } }, rowModesModel));
    }
  };

  return [
    {
      field: 'segmentName',
      flex: 1,
      headerName: 'Segment',
      minWidth,
    },
    {
      field: 'shipperName',
      flex: 1,
      headerName: 'Shipper',
      minWidth,
    },
    {
      aggregable: false,
      field: 'productName',
      flex: 1,
      headerName: 'Product Grade',
      minWidth,
      renderCell: ({ value }) => <ProductGradeChip value={value} />,
    },
    {
      align: 'left',
      editable: false,
      field: 'volNominated',
      flex: 1,
      headerAlign: 'left',
      headerName: 'Vol. Nominated (bbl)',
      minWidth,
      type: 'number',
    },
    {
      align: 'left',
      field: 'volAwarded',
      flex: 1,
      headerAlign: 'left',
      headerName: 'Vol. Awarded (bbl)',
      minWidth,
      type: 'number',
    },
    {
      aggregable: false,
      align: 'left',
      editable: true,
      field: 'volAccepted',
      flex: 1,
      headerAlign: 'left',
      headerName: 'Vol. Accepted (bbl)',
      minWidth,
      preProcessEditCellProps: params => {
        const { props, row } = params;

        const hasError = [
          R.isNil(props.value),
          R.not(isInt(props.value)),
          R.gt(0, props.value),
          R.gt(props.value, row.volAwarded),
        ].includes(true);

        setNominations(
          R.mergeDeepLeft(
            {
              [row.nominationId]: {
                hasChanged: row.volAccepted !== props.value,
                isValid: !hasError,
                nominationId: row.nominationId,
                volAccepted: props.value,
              },
            },
            nominations
          )
        );

        return { ...params.props, error: hasError };
      },
      renderEditCell: params => {
        const {
          error,
          row: { volAwarded },
          value,
        } = params;
        const title = error && getValidationMessage(value, volAwarded);

        return (
          <Tooltip
            arrow
            open={error}
            slotProps={{
              popper: {
                sx: {
                  '& .MuiTooltip-arrow': {
                    color: 'error.main',
                  },
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: 'error.main',
                    color: 'error.contrastText',
                  },
                },
              },
            }}
            title={title}
          >
            <Box sx={{ height: '100%' }}>
              <GridEditInputCell
                {...params}
                sx={{
                  '&.Mui-error': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                  backgroundColor: 'secondary.light',
                  height: '100%',
                }}
              />
            </Box>
          </Tooltip>
        );
      },
      type: 'number',
    },
    {
      field: 'nomMonth',
      flex: 1,
      headerName: 'Nom. Month',
      minWidth,
    },
    {
      disableExport: true,
      field: 'action',
      getActions: params => {
        const { row } = params;
        const { nominationId, nomMonth } = row;

        if (isProductShippingViewOnly || R.isNil(nominationId)) return [];

        const isInEditMode = rowModesModel[nominationId]?.mode === GridRowModes.Edit;

        return [
          ...(isInEditMode
            ? [
                <GridActionsCellItem
                  disabled={!nominations[nominationId].isValid}
                  icon={<SaveIcon />}
                  label="Save"
                  onClick={handleSave(nominationId)}
                />,
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label="Cancel"
                  onClick={handleCancel(nominationId)}
                />,
              ]
            : new Date() < new Date(nomMonth)
            ? [<Button onClick={handleElect(row)}>Elect</Button>]
            : []),
          <GridActionsCellItem
            icon={<FileCopyIcon />}
            label="Copy"
            onClick={() => handleCopyNomination(nominationId)}
          />,
        ];
      },
      headerName: 'Elect',
      minWidth,
      type: 'actions',
    },
  ];
};

export default AwardedColumns;
